import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { TopBar } from './components/TopBar';
import { RailPage } from './components/RailPage';
import { ActivationToast } from './components/ActivationToast';
import AdminApp from './pages/admin/AdminApp';
import { Login } from './pages/Login';
import { ResetPassword } from './pages/ResetPassword';
import { Pricing } from './pages/Pricing';
import { Studio } from './pages/Studio';
import { StudioNext } from './pages/StudioNext';
import { Dashboard } from './pages/Dashboard';
import { Sections } from './pages/Sections';
import { Billing } from './pages/Billing';
import { Settings } from './pages/Settings';
import { Tos } from './pages/Tos';
import { Privacy } from './pages/Privacy';
import { Support } from './pages/Support';
import { ExtensionAuth } from './pages/ExtensionAuth';
import { Checkout } from './pages/Checkout';
import { Splitter } from './pages/Splitter';
// EXPERIMENT (not linked from anywhere): the Studio with the auto-split upload
// onboarding — a dropped file's busiest minute separates at once, free, and the
// offer waits for the edge of that minute. A COPY of StudioNext, loaded only
// when /upload-test is opened, so '/' neither runs nor downloads any of it.
const StudioAutoSplit = lazy(() => import('./pages/StudioAutoSplit').then((m) => ({ default: m.StudioAutoSplit })));

function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Splash />;
  // Carry the current path+query (e.g. /?handoff=… from the splitter funnel,
  // /?src=… from a phone→computer hand-off) so a momentary auth miss right
  // after the OAuth redirect doesn't discard it.
  //
  // ⚠️ BOTH channels are needed. Router state is in-memory: it survives the
  // client-side bounce and the email+password sign-in, but Google sign-in is a
  // full page navigation away and back, and everything not in the URL is gone
  // by the time we return. Login already reads `?next=` (same-origin paths
  // only) and passes it through the OAuth round trip, so putting the
  // destination there as well is what makes the Google path keep it. Purely
  // additive — `state.from` is still the fallback Login reaches for second.
  if (!user) {
    const from = location.pathname + location.search;
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(from)}`}
        replace
        state={{ from }}
      />
    );
  }
  return <>{children}</>;
}

function Splash() {
  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <div className="rl-spinner" />
    </div>
  );
}

export default function App() {
  // The owner admin subsite is a SEPARATE app with its own auth + chrome — hide
  // the user-facing TopBar there (it has no Google session and its own header).
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');
  // The rail replaced the TopBar on every signed-in app page. What's left on
  // the TopBar is the public / single-purpose flows: sign-in, checkout, the
  // legal pages and the extension handoff, where a full app nav would offer
  // links a logged-out visitor can't open.
  // Only routes that actually render a rail. /studio-legacy and /soundcloud
  // still serve the previous Studio, which has none — they keep the TopBar or
  // they'd have no navigation at all.
  const RAIL_ROUTES = ['/', '/songs', '/studio-next', '/upload-test', '/dashboard', '/sections', '/billing', '/settings', '/support', '/pricing'];
  const hasRail = RAIL_ROUTES.includes(pathname);
  // /login is a full-bleed split screen that carries its own logo and legal
  // links, so the TopBar would only duplicate them above the hero. It rendered
  // nothing else there anyway — logged out, the bar is the logo plus the theme
  // toggle, and the toggle is moot on a page that is dark in both themes.
  const isLogin = pathname === '/login';
  // /splitter carries its OWN header. The shared TopBar renders no navigation
  // at all for a logged-out visitor (by design: everything it links to needs an
  // account), and that page's whole audience is logged out — it arrives from an
  // SEO article and needs a menu that points at pages it can actually open.
  const isSplitter = pathname === '/splitter';
  return (
    <AuthProvider>
      {/* Route-agnostic: the activation redirect lands on '/', but this keeps
          working if that destination ever changes. */}
      {!isAdmin && <ActivationToast />}
      {!isAdmin && !hasRail && !isLogin && !isSplitter && <TopBar />}
      <Routes>
        {/* Owner-only admin — gated server-side; this route is useless without
            a completed admin session. NOT wrapped in the Google <Protected>. */}
        <Route path="/admin/*" element={<AdminApp />} />
        {/* The Studio IS the root of studio.riffloop.app — no /studio path
            stutter (owner's call, 2026-06-11). Sign-in only: logged-out
            visitors land on /login.
            As of the 2026-08-09 cutover this serves the redesigned Studio
            (rail + docked player). The previous one is still built and routed
            at /studio-legacy, so a rollback is a one-line route change rather
            than a rebuild. */}
        <Route
          path="/"
          element={
            <Protected>
              <StudioNext />
            </Protected>
          }
        />
        {/* The songs table — the same view the Studio's rail shows, so
            "Your songs" means one thing wherever it's clicked. */}
        <Route
          path="/songs"
          element={
            <Protected>
              <StudioNext initialScreen="library" />
            </Protected>
          }
        />
        <Route
          path="/studio-legacy"
          element={
            <Protected>
              <Studio />
            </Protected>
          }
        />
        {/* Hidden work-in-progress: the Studio with the SoundCloud tab exposed.
            Same component as "/", just reveals the extra tab — not linked in the
            nav, so live users on "/" never see it. Promote by rendering the tab
            unconditionally in Studio. */}
        <Route
          path="/soundcloud"
          element={
            <Protected>
              <Studio />
            </Protected>
          }
        />
        {/* Was the preview URL before the cutover; kept so existing bookmarks
            and the /next/ build both still land somewhere sensible. */}
        <Route
          path="/studio-next"
          element={
            <Protected>
              <StudioNext />
            </Protected>
          }
        />
        {/* EXPERIMENT (see the lazy import above): the auto-split upload flow,
            for the owner to test before anything changes at '/'. */}
        <Route
          path="/upload-test"
          element={
            <Protected>
              <Suspense fallback={<Splash />}>
                <StudioAutoSplit />
              </Suspense>
            </Protected>
          }
        />
        <Route path="/login" element={<Login />} />
        {/* Landing page for the reset link in the password email. Public by
            necessity — whoever follows it is signed OUT and the one-time token
            in the URL is the only credential they have. */}
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Extension sign-in handoff — self-handles auth, not Protected. */}
        <Route path="/extension-auth" element={<ExtensionAuth />} />
        {/* Web checkout deep-link (homepage "Get Pro" → here). Public: it
            handles its own auth so it can carry the checkout intent through
            login, rather than losing it to Protected's bounce. */}
        <Route path="/checkout" element={<Checkout />} />
        {/* In-app upgrade page — logged-in only, uses the app's TopBar for
            navigation back to the editor (owner's call, 2026-06-13). The
            marketing pricing lives on riffloop.app. */}
        <Route
          path="/pricing"
          element={
            <Protected>
              <RailPage><Pricing /></RailPage>
            </Protected>
          }
        />
        <Route path="/tos" element={<Tos />} />
        <Route path="/privacy" element={<Privacy />} />

        <Route
          path="/dashboard"
          element={
            <Protected>
              <RailPage><Dashboard /></RailPage>
            </Protected>
          }
        />
        {/* Old bookmarks / the PHP callback's previous target. */}
        <Route path="/studio" element={<Navigate to="/" replace />} />
        {/* EXPERIMENT (not linked from anywhere): the LALAL-shaped funnel —
            anonymous upload → the first 60s separated free and immediately →
            an ask only after that. Deliberately NOT <Protected>: being able to
            reach the result without an account is the thing under test. '/'
            is untouched and doesn't know this exists. */}
        <Route path="/splitter" element={<Splitter />} />
        <Route
          path="/sections"
          element={
            <Protected>
              <RailPage><Sections /></RailPage>
            </Protected>
          }
        />
        <Route
          path="/billing"
          element={
            <Protected>
              <RailPage><Billing /></RailPage>
            </Protected>
          }
        />
        <Route
          path="/settings"
          element={
            <Protected>
              <RailPage><Settings /></RailPage>
            </Protected>
          }
        />
        <Route
          path="/support"
          element={
            <Protected>
              <RailPage><Support /></RailPage>
            </Protected>
          }
        />

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AuthProvider>
  );
}
